import { useMemo } from "react";
import useSWR from "swr";
import { parseUnits } from "viem";

import { getServerUrl } from "config/backend";
import { useGetLeaderboard } from "lib/apis/leaderboard/queries";
import { LeaderboardApiResponseItem } from "lib/apis/leaderboard/types";
import { USD_DECIMALS } from "lib/numbers";

import { LeaderboardDataType } from "./types";

export * from "./types";

export type LeaderboardAccountBase = {
  account: string;
  username?: string;
  cumsumCollateral: bigint;
  cumsumSize: bigint;
  sumMaxSize: bigint;

  maxCapital: bigint;
  netCapital: bigint;
  hasRank: boolean;

  realizedPriceImpact: bigint;
  realizedFees: bigint;
  realizedPnl: bigint;

  startUnrealizedPnl: bigint;
  startUnrealizedPriceImpact: bigint;
  startUnrealizedFees: bigint;

  closedCount: number;
  volume: bigint;
  losses: number;
  wins: number;
};

export type LeaderboardAccount = LeaderboardAccountBase & {
  totalCount: number;
  totalPnl: bigint;
  totalQualifyingPnl: bigint;
  totalFees: bigint;
  unrealizedFees: bigint;
  unrealizedPnl: bigint;
  pnlPercentage: bigint;
  averageSize: bigint;
  averageLeverage: bigint;
};

export type LeaderboardPositionBase = {
  key: string;
  account: string;
  realizedFees: bigint;
  unrealizedFees: bigint;
  isLong: boolean;
  market: string;
  maxSize: bigint;
  realizedPriceImpact: bigint;
  unrealizedPriceImpact: bigint;
  isSnapshot: boolean;
  unrealizedPnl: bigint;
  realizedPnl: bigint;
  sizeInTokens: bigint;
  sizeInUsd: bigint;
  entryPrice: bigint;
  collateralToken: string;
  collateralAmount: bigint;
  snapshotTimestamp: number;
};

export type LeaderboardPosition = LeaderboardPositionBase & {
  rank: number;
  fees: bigint;
  pnl: bigint;
  qualifyingPnl: bigint;
  leverage: bigint;
  collateralUsd: bigint;
  closingFeeUsd: bigint;
};

function mapApiItemToAccount(item: LeaderboardApiResponseItem): LeaderboardAccount {
  // Use parseUnits to handle decimal strings like "0.00" or "150.5"
  // API returns USD values as strings, likely needing 30 decimals for internal logic
  let pnl = 0n;
  try {
    pnl = parseUnits(item.pnlUsd || "0", USD_DECIMALS);
  } catch (e) {
    console.error("Failed to parse pnl", item.pnlUsd, e);
  }

  let volume = 0n;
  try {
    volume = parseUnits(item.volumeUsd || "0", USD_DECIMALS);
  } catch (e) {
    console.error("Failed to parse volume", item.volumeUsd, e);
  }

  let averageSize = 0n;
  try {
    averageSize = parseUnits(item.averageSizeUsd || "0", USD_DECIMALS);
  } catch (e) {
    console.error("Failed to parse average size", item.averageSizeUsd, e);
  }

  let roi = 0n;
  try {
    roi = parseUnits(item.roi || "0", USD_DECIMALS);
  } catch (e) {
    console.error("Failed to parse roi", item.roi, e);
  }

  let averageLeverage = 0n;
  try {
    averageLeverage = parseUnits(item.averageLeverage || "0", 4);
  } catch (e) {
    console.error("Failed to parse average leverage", item.averageLeverage, e);
  }

  // Derive wins/losses from tradesCount and winRate
  // winRate is "65.5" (percent)
  const winRate = Number.parseFloat(item.winRate);
  const totalTrades = item.tradesCount;
  const normalizedWinRate = Number.isFinite(winRate) ? winRate : 0;
  const wins = Math.round((totalTrades * normalizedWinRate) / 100);
  const losses = totalTrades - wins;
  const scale = 10n ** BigInt(USD_DECIMALS);
  const maxCapital = roi !== 0n ? (bigintAbs(pnl) * 100n * scale) / bigintAbs(roi) : 0n;

  return {
    account: item.account,
    username: item.username ?? item.referralCode,
    cumsumCollateral: 0n, // Unknown
    cumsumSize: 0n, // Unknown
    sumMaxSize: averageSize * BigInt(totalTrades), // Preserve average size through legacy selector recomputation
    maxCapital,
    netCapital: maxCapital,
    hasRank: true,
    realizedPriceImpact: 0n,
    realizedFees: 0n,
    realizedPnl: pnl, // Assume all realized for now
    startUnrealizedPnl: 0n,
    startUnrealizedPriceImpact: 0n,
    startUnrealizedFees: 0n,
    closedCount: totalTrades,
    volume: volume,
    losses: losses,
    wins: wins,
    totalCount: totalTrades,
    totalPnl: pnl,
    totalQualifyingPnl: pnl, // Sorting key
    totalFees: 0n,
    unrealizedFees: 0n,
    unrealizedPnl: 0n,
    pnlPercentage: roi, // Sorting key
    averageSize: averageSize > 0n ? averageSize : totalTrades > 0 ? volume / BigInt(totalTrades) : 0n,
    averageLeverage: averageLeverage,
  };
}

function bigintAbs(value: bigint) {
  return value < 0n ? -value : value;
}

export function useLeaderboardData(
  enabled: boolean,
  chainId: number,
  p: {
    account: string | undefined;
    from: number;
    to: number | undefined;
    positionsSnapshotTimestamp: number | undefined;
    leaderboardDataType: LeaderboardDataType | undefined;
  }
) {
  const {
    data: apiData,
    error: apiError,
    isLoading: apiLoading,
  } = useGetLeaderboard(
    chainId,
    {
      from: p.from,
      to: p.to,
      limit: 100,
    },
    enabled && (!p.leaderboardDataType || p.leaderboardDataType === "accounts")
  );

  // Deduplicate by account address — the API can return the same address
  // multiple times (different period snapshots) which creates duplicate rows.
  const accounts: LeaderboardAccount[] | undefined = useMemo(() => {
    if (!apiData?.items) return undefined;
    const seen = new Map<string, LeaderboardAccount>();
    for (const item of apiData.items) {
      if (!seen.has(item.account)) {
        seen.set(item.account, mapApiItemToAccount(item));
      }
    }
    return Array.from(seen.values());
  }, [apiData]);

  const {
    data: positionsData,
    error: positionsError,
    isLoading: positionsLoading,
  } = useSWR(
    enabled && p.leaderboardDataType === "positions"
      ? ["leaderboard/positions", chainId, p.positionsSnapshotTimestamp]
      : null,
    () => fetchPositions(chainId, p.positionsSnapshotTimestamp)
  );

  if (p.leaderboardDataType === "positions") {
    return {
      data: { positions: positionsData, accounts: undefined },
      error: positionsError,
      isLoading: positionsLoading,
    };
  }

  return {
    data: {
      accounts,
      positions: undefined,
    },
    error: apiError,
    isLoading: apiLoading,
  };
}

/**
 * MIGRATED: fetchPositions now uses the /leaderboard/positions REST endpoint.
 */
const fetchPositions = async (
  chainId: number,
  snapshotTimestamp: number | undefined
): Promise<LeaderboardPositionBase[] | undefined> => {
  try {
    const url = new URL(getServerUrl(chainId, "/leaderboard/positions"));
    url.searchParams.set("limit", "100");
    if (snapshotTimestamp) {
      url.searchParams.set("snapshotTimestamp", snapshotTimestamp.toString());
    }

    const response = await fetch(url.toString());
    const json: { items: any[] } = await response.json();

    return json.items.map((p) => ({
      key: p.id,
      account: p.account,
      market: p.market,
      collateralToken: p.collateralToken,
      isLong: p.isLong,
      realizedPriceImpact: 0n, // Not currently in REST response
      realizedFees: BigInt(p.realizedFees || "0"),
      collateralAmount: BigInt(p.collateralAmount || "0"),
      unrealizedFees: BigInt(p.unrealizedFees || "0"),
      entryPrice: BigInt(p.entryPrice || "0"),
      sizeInUsd: BigInt(p.sizeInUsd || "0"),
      sizeInTokens: BigInt(p.sizeInTokens || "0"),
      realizedPnl: BigInt(p.realizedPnl || "0"),
      unrealizedPriceImpact: 0n, // Not currently in REST response
      unrealizedPnl: BigInt(p.unrealizedPnl || "0"),
      maxSize: BigInt(p.maxSize || "0"),
      snapshotTimestamp: snapshotTimestamp || 0,
      isSnapshot: !!snapshotTimestamp,
    }));
  } catch (e) {
    console.error("Failed to fetch leaderboard positions", e);
    return [];
  }
};
